/**
 * WorkshopEarningsDashboard - Practitioner earnings and sales analytics
 * Shows workshop sales history, earnings breakdown, pending/paid amounts
 */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, ShoppingBag, Clock, CheckCircle,
  BarChart3, Calendar, Users, BookOpen, Loader2, ArrowUpRight,
  Wallet, PiggyBank, CreditCard
} from 'lucide-react';

const WorkshopEarningsDashboard = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [salesHistory, setSalesHistory] = useState([]);
  const [workshopStats, setWorkshopStats] = useState([]);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Generate month options for filter
  const getMonthOptions = () => {
    const options = [{ value: 'all', label: 'All Time' }];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7); // YYYY-MM
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };
  
  const fetchEarningsData = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      if (selectedMonth !== 'all') params.append('month', selectedMonth);
      
      const [earningsRes, salesRes, statsRes] = await Promise.all([
        axios.get(`${API}/workshops/earnings/summary?${params}`, { headers }),
        axios.get(`${API}/workshops/earnings/sales?${params}`, { headers }),
        axios.get(`${API}/workshops/earnings/workshop-stats`, { headers })
      ]);
      
      setEarnings(earningsRes.data);
      setSalesHistory(salesRes.data);
      setWorkshopStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch earnings data:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);
  
  useEffect(() => {
    fetchEarningsData();
  }, [fetchEarningsData]);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <Card className="border-2 border-green-500/30">
        <CardContent className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-green-500" />
          <p className="mt-2 text-gray-400">Loading earnings data...</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="workshop-earnings-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Wallet className="w-6 h-6 text-green-400" />
            Workshop Earnings
          </h2>
          <p className="text-gray-400">Track your workshop sales and earnings</p>
        </div>
        
        {/* Month Filter */}
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-48 text-white" data-testid="month-filter">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            {getMonthOptions().map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Earnings */}
        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-300 mb-1">Total Earnings</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(earnings?.total_earnings)}
                </p>
                <p className="text-xs text-gray-400 mt-1">Your share of sales</p>
              </div>
              <div className="p-3 bg-green-500/20 rounded-full">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Pending Payout */}
        <Card className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 border-yellow-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-300 mb-1">Pending Payout</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(earnings?.pending_amount)}
                </p>
                <p className="text-xs text-gray-400 mt-1">Awaiting payment</p>
              </div>
              <div className="p-3 bg-yellow-500/20 rounded-full">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Paid Out */}
        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-300 mb-1">Paid Out</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(earnings?.paid_amount)}
                </p>
                <p className="text-xs text-gray-400 mt-1">Already received</p>
              </div>
              <div className="p-3 bg-blue-500/20 rounded-full">
                <CheckCircle className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Total Sales Count */}
        <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border-purple-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-300 mb-1">Total Sales</p>
                <p className="text-3xl font-bold text-white">
                  {earnings?.total_sales || 0}
                </p>
                <p className="text-xs text-gray-400 mt-1">Workshops purchased</p>
              </div>
              <div className="p-3 bg-purple-500/20 rounded-full">
                <ShoppingBag className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Workshop Performance */}
      {workshopStats.length > 0 && (
        <Card className="border-slate-700 bg-slate-800/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              Workshop Performance
            </CardTitle>
            <CardDescription>Sales breakdown by workshop</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {workshopStats.map((workshop) => (
                <div 
                  key={workshop.workshop_id}
                  className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-4 h-4 text-purple-400" />
                      <span className="font-medium text-white">{workshop.title}</span>
                      {workshop.is_collaborative && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {workshop.your_percentage}% share
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>{workshop.sales_count} sales</span>
                      <span>•</span>
                      <span>{formatCurrency(workshop.total_revenue)} revenue</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-green-400">
                      {formatCurrency(workshop.your_earnings)}
                    </p>
                    <p className="text-xs text-gray-500">Your earnings</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Sales History */}
      <Card className="border-slate-700 bg-slate-800/50">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            Sales History
          </CardTitle>
          <CardDescription>Individual sale transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {salesHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400">Workshop</TableHead>
                    <TableHead className="text-gray-400">Buyer</TableHead>
                    <TableHead className="text-gray-400 text-right">Sale Price</TableHead>
                    <TableHead className="text-gray-400 text-right">Your Share</TableHead>
                    <TableHead className="text-gray-400 text-right">Your Earnings</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesHistory.map((sale) => (
                    <TableRow key={sale.id} className="border-slate-700">
                      <TableCell className="text-white">
                        {formatDate(sale.purchase_date)}
                      </TableCell>
                      <TableCell className="text-white font-medium">
                        {sale.workshop_title}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {sale.buyer_name || 'Anonymous'}
                      </TableCell>
                      <TableCell className="text-right text-white">
                        {formatCurrency(sale.total_price)}
                      </TableCell>
                      <TableCell className="text-right text-gray-400">
                        {sale.your_percentage}%
                      </TableCell>
                      <TableCell className="text-right text-green-400 font-medium">
                        {formatCurrency(sale.your_earnings)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={
                            sale.payout_status === 'paid' 
                              ? 'bg-green-600 text-white' 
                              : 'bg-yellow-600 text-white'
                          }
                        >
                          {sale.payout_status === 'paid' ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Paid</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No sales yet</p>
              <p className="text-sm text-gray-500 mt-1">
                Sales will appear here once users purchase your workshops
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Monthly Earnings Breakdown */}
      {earnings?.monthly_breakdown && earnings.monthly_breakdown.length > 0 && (
        <Card className="border-slate-700 bg-slate-800/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-cyan-400" />
              Monthly Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {earnings.monthly_breakdown.map((month) => (
                <div 
                  key={month.month}
                  className="bg-slate-700/30 rounded-lg p-3 text-center"
                >
                  <p className="text-xs text-gray-400 mb-1">
                    {new Date(month.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
                  </p>
                  <p className="text-lg font-bold text-green-400">
                    {formatCurrency(month.earnings)}
                  </p>
                  <p className="text-xs text-gray-500">{month.sales} sales</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WorkshopEarningsDashboard;
